import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyPunchQr } from '../services/kioskService';
import QrCode from '../components/QrCode';

/**
 * 自分の打刻用QRを表示する画面（スマートフォン想定）。
 *
 * 共用端末の前でこの画面を開き、カメラにかざして打刻する。
 *
 * ## なぜ「スマホがあるのに端末で打つ」のか
 *
 * 一見冗長だが、**会社が「端末で打刻すること」を運用ルールにしている場合**がある。
 * このときスマホは資格情報を運ぶ道具にすぎず、時刻の正しさを担保するのは端末側。
 * 「打刻はサーバー時刻」という前提を保ったまま、本人特定だけを速くできる（設計書 §13-4）。
 *
 * ## 表示するたびに値は変わらない
 *
 * 管理者が印刷して配ったQRと同じ値を出す。ここで作り直すと紙のQRが死ぬ。
 */
const MyPunchQrPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyPunchQr()
      .then((r) => setToken(r.qrToken))
      .catch((e) => setError(e.response?.data?.error || 'QRコードを取得できませんでした'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={s.root}>
      <div style={s.card}>
        <h1 style={s.title}>打刻用QRコード</h1>

        {loading && <p style={s.note}>読み込み中...</p>}
        {error && <p style={s.error}>{error}</p>}

        {token && (
          <>
            <div style={s.qrWrap}>
              <QrCode value={token} size={240} title="自分の打刻用QRコード" />
            </div>
            <p style={s.name}>{user?.name}</p>
            <p style={s.note}>
              共用端末のカメラにこの画面をかざしてください。
              <br />
              画面の明るさを上げると読み取りやすくなります。
            </p>
          </>
        )}

        <button onClick={() => navigate('/dashboard')} style={s.backBtn}>
          ダッシュボードに戻る
        </button>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #dee2e6',
    padding: 'clamp(20px, 5vw, 32px)',
    width: 'min(94vw, 420px)',
    textAlign: 'center',
  },
  title: { margin: '0 0 16px', fontSize: 'clamp(17px, 4vw, 20px)' },
  qrWrap: { display: 'flex', justifyContent: 'center', padding: 8 },
  name: { fontSize: 'clamp(18px, 4.5vw, 22px)', fontWeight: 700, margin: '12px 0 4px' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.8, margin: '8px 0 0' },
  error: { color: '#721c24', background: '#f8d7da', borderRadius: 4, padding: 10, fontSize: 14 },
  backBtn: {
    marginTop: 20,
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '10px 20px',
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default MyPunchQrPage;
